import React, { createContext, useCallback, useContext, useEffect, useRef, useState } from 'react';
import api from '../../../services/api';
import type {
  CustomerOrder, EquipmentOverview, FactoryMaterial, GrindingTrip, Machine, MixBatch, ProductionKpis, Reception,
  ShiftEntry, StockStatus, StockStatusItem, StorageLocation,
} from '../../../types/factory';
import type { Tone } from '../../../components/ui/PageKit';
import { addDaysIso, fmtQty, todayIso } from '../shared';
import type { PlantLayout, PlantZone } from './layouts';

export interface PlantData {
  machines: Machine[];
  overview: EquipmentOverview[];
  stock: StockStatus;
  materials: FactoryMaterial[];
  kpisToday: ProductionKpis | null;
  shifts: ShiftEntry[];
  locations: StorageLocation[];
  receptions: Reception[];
  mixes: MixBatch[];
  grinding: GrindingTrip[];
  orders: CustomerOrder[];
  today: string;
}

const EMPTY: PlantData = {
  machines: [], overview: [], stock: { products: [], materials: [] }, materials: [], kpisToday: null, shifts: [],
  locations: [], receptions: [], mixes: [], grinding: [], orders: [], today: todayIso(),
};

function settled<T>(r: PromiseSettledResult<T>, fallback: T): T {
  return r.status === 'fulfilled' ? r.value : fallback;
}

export interface PlantDataState {
  data: PlantData;
  loading: boolean;
  updatedAt: Date | null;
  failed: number;
  reload: () => Promise<void>;
}

const PlantDataContext = createContext<PlantDataState | null>(null);

/**
 * El sistema de planta comparte un solo juego de datos entre la barra (campanita), el tablero y el plano,
 * así no se piden las mismas 11 consultas por duplicado.
 */
export const PlantDataProvider: React.FC<{ refreshMs?: number; children: React.ReactNode }> = ({ refreshMs = 60_000, children }) => {
  const value = usePlantDataSource(refreshMs, true);
  return React.createElement(PlantDataContext.Provider, { value }, children);
};

/** Datos de planta: los del sistema de planta si hay uno arriba; si no, los pide esta pantalla. */
export function usePlantData(refreshMs = 60_000): PlantDataState {
  const shared = useContext(PlantDataContext);
  const own = usePlantDataSource(refreshMs, !shared);
  return shared ?? own;
}

/** Todo lo que muestra el plano, en paralelo. Si un pedido falla, ese sector queda vacío y el resto anda. */
function usePlantDataSource(refreshMs: number, enabled: boolean): PlantDataState {
  const [data, setData] = useState<PlantData>(EMPTY);
  const [loading, setLoading] = useState(true);
  const [updatedAt, setUpdatedAt] = useState<Date | null>(null);
  const [failed, setFailed] = useState(0);
  const busy = useRef(false);

  const load = useCallback(async () => {
    if (busy.current) return;
    busy.current = true;
    const today = todayIso();
    const r = await Promise.allSettled([
      api.getMachines(),
      api.getMaintenanceOverview(),
      api.getStockStatus(),
      api.getFactoryMaterials(),
      api.getProductionKpis({ from: today, to: today }),
      api.getShiftEntries({ from: addDaysIso(today, -6), to: today }),
      api.getStorageLocations(),
      api.getReceptions({ from: addDaysIso(today, -45), to: today }),
      api.getMixBatches({ from: addDaysIso(today, -14), to: today }),
      api.getGrindingTrips(),
      api.getCustomerOrders({ status: 'Open' }),
    ]);
    setFailed(r.filter(x => x.status === 'rejected').length);
    setData({
      machines: settled(r[0], [] as Machine[]),
      overview: settled(r[1], [] as EquipmentOverview[]),
      stock: settled(r[2], { products: [], materials: [] } as StockStatus),
      materials: settled(r[3], [] as FactoryMaterial[]),
      kpisToday: settled(r[4], null as ProductionKpis | null),
      shifts: settled(r[5], [] as ShiftEntry[]),
      locations: settled(r[6], [] as StorageLocation[]),
      receptions: settled(r[7], [] as Reception[]),
      mixes: settled(r[8], [] as MixBatch[]),
      grinding: settled(r[9], [] as GrindingTrip[]),
      orders: settled(r[10], [] as CustomerOrder[]),
      today,
    });
    setUpdatedAt(new Date());
    setLoading(false);
    busy.current = false;
  }, []);

  useEffect(() => {
    if (!enabled) return;
    load();
    const t = window.setInterval(() => { if (!document.hidden) load(); }, refreshMs);
    const onVisible = () => { if (!document.hidden) load(); };
    document.addEventListener('visibilitychange', onVisible);
    return () => { window.clearInterval(t); document.removeEventListener('visibilitychange', onVisible); };
  }, [load, refreshMs, enabled]);

  return { data, loading, updatedAt, failed, reload: load };
}

// ==================== Qué hay en cada sector ====================

export const isMold = (m: Machine) => (m.kind || '').toLowerCase() === 'matriz';

export function findMachine(zone: PlantZone, machines: Machine[]): Machine | undefined {
  if (!zone.machine) return undefined;
  return machines.find(m => !isMold(m) && (zone.machine!.test(m.name) || (!!m.code && zone.machine!.test(m.code))));
}

export const productsOf = (zone: PlantZone, d: PlantData): StockStatusItem[] =>
  zone.products ? d.stock.products.filter(p => zone.products!.test(p.name)) : [];

export function materialsOf(zone: PlantZone, d: PlantData): FactoryMaterial[] {
  return d.materials.filter(m =>
    (zone.origins ? !!m.origin && zone.origins.includes(m.origin) : true) &&
    (zone.materials ? zone.materials.test(m.name) : true) &&
    (zone.origins || zone.materials ? true : false));
}

export const locationsOf = (zone: PlantZone, d: PlantData): StorageLocation[] =>
  zone.sector ? d.locations.filter(l => zone.sector!.test(`${l.sector || ''} ${l.code} ${l.name || ''}`)) : [];

export const molds = (d: PlantData) => d.machines.filter(isMold);

export const shiftsToday = (d: PlantData, machineId?: number) =>
  d.shifts.filter(s => s.date.slice(0, 10) === d.today && (machineId == null || s.machineId === machineId));

/** Peor semáforo de un grupo de ítems de stock. */
export function worstLevel(items: StockStatusItem[]): Tone {
  if (items.some(i => i.level === 'rojo')) return 'bad';
  if (items.some(i => i.level === 'amarillo')) return 'warn';
  if (items.some(i => i.level === 'verde')) return 'ok';
  return 'neutral';
}

export interface ZoneStatus {
  tone: Tone;
  label: string;
  metric?: string;
  /** Algo se está moviendo ahí ahora (máquina con parte hoy). */
  active?: boolean;
}

const MACHINE_STATUS: Record<string, { tone: Tone; label: string }> = {
  FueraDeServicio: { tone: 'bad', label: 'Fuera de servicio' },
  EnMantenimiento: { tone: 'warn', label: 'En mantenimiento' },
};

/** Estado de un sector para el plano: color del punto, texto corto y un número. */
export function zoneStatus(zone: PlantZone, d: PlantData): ZoneStatus {
  switch (zone.kind) {
    case 'machine': {
      const m = findMachine(zone, d.machines);
      if (!m) return { tone: 'neutral', label: 'Sin cargar en el sistema' };
      const ov = d.overview.find(o => o.machineId === m.id);
      const good = shiftsToday(d, m.id).reduce((a, s) => a + s.goodQuantity, 0);
      const metric = good > 0 ? `${fmtQty(good)} u. hoy` : undefined;
      if (MACHINE_STATUS[m.status]) return { ...MACHINE_STATUS[m.status], metric };
      if (ov?.maintenanceStatus === 'vencido') return { tone: 'bad', label: 'Mantenimiento vencido', metric, active: good > 0 };
      if (good > 0) return { tone: 'ok', label: 'Produciendo', metric, active: true };
      if (ov?.maintenanceStatus === 'proximo') return { tone: 'warn', label: 'Mantenimiento próximo' };
      return { tone: 'neutral', label: 'Sin parte hoy' };
    }
    case 'products': {
      const items = productsOf(zone, d);
      if (!items.length) return { tone: 'neutral', label: 'Sin productos' };
      const total = items.reduce((a, i) => a + i.stock, 0);
      const tone = worstLevel(items);
      const label = tone === 'bad' ? 'Hay productos sin stock' : tone === 'warn' ? 'Stock cerca del mínimo' : tone === 'ok' ? 'Stock suficiente' : 'Sin niveles configurados';
      return { tone, label, metric: `${fmtQty(total)} u.` };
    }
    case 'materials':
    case 'inputs': {
      const mats = materialsOf(zone, d);
      if (!mats.length) return { tone: 'neutral', label: zone.origins ? 'Sin MP con ese origen' : 'Sin insumos' };
      const ids = new Set(mats.map(m => m.id));
      const tone = worstLevel(d.stock.materials.filter(i => ids.has(i.id)));
      const kg = mats.reduce((a, m) => a + m.currentStock, 0);
      const unit = zone.kind === 'materials' ? 'kg' : 'u.';
      return { tone, label: tone === 'bad' ? 'Hay faltantes' : tone === 'warn' ? 'Cerca del mínimo' : 'Disponible', metric: `${fmtQty(kg)} ${unit}` };
    }
    case 'mix': {
      const week = d.mixes.filter(x => x.date.slice(0, 10) >= addDaysIso(d.today, -6));
      const kg = week.reduce((a, x) => a + x.totalKg, 0);
      return { tone: week.length ? 'ok' : 'neutral', label: week.length ? `${week.length} mezclas en la semana` : 'Sin mezclas en la semana', metric: kg ? `${fmtQty(kg)} kg` : undefined };
    }
    case 'grinding': {
      const out = d.grinding.filter(g => g.status === 'Enviado');
      const kg = out.reduce((a, g) => a + g.sentKg, 0);
      const late = out.some(g => (Date.now() - new Date(g.sentAt).getTime()) / 86_400_000 > 7);
      if (out.length) return { tone: late ? 'warn' : 'info', label: late ? 'Molienda afuera hace más de 7 días' : `${out.length} viaje${out.length > 1 ? 's' : ''} en molienda`, metric: `${fmtQty(kg)} kg afuera`, active: true };
      return { tone: 'neutral', label: 'Nada en molienda' };
    }
    case 'molds': {
      const ms = molds(d);
      if (!ms.length) return { tone: 'neutral', label: 'Sin matrices cargadas' };
      const ids = new Set(ms.map(m => m.id));
      const ov = d.overview.filter(o => ids.has(o.machineId));
      const bad = ms.some(m => m.status === 'FueraDeServicio') || ov.some(o => o.maintenanceStatus === 'vencido');
      const warn = ms.some(m => m.status === 'EnMantenimiento') || ov.some(o => o.maintenanceStatus === 'proximo');
      const used = ms.filter(m => m.mountedOnId).length;
      return { tone: bad ? 'bad' : warn ? 'warn' : 'ok', label: bad ? 'Matriz con problema' : warn ? 'Matriz en reparación o por mantener' : 'Matrices al día', metric: `${used}/${ms.length} en uso` };
    }
    case 'scale': {
      const todays = d.receptions.filter(r => isTodayAr(r.receivedAt, d.today));
      const kg = todays.reduce((a, r) => a + r.kg, 0);
      return { tone: todays.length ? 'info' : 'neutral', label: todays.length ? `${todays.length} pesaje${todays.length > 1 ? 's' : ''} hoy` : 'Sin pesajes hoy', metric: kg ? `+${fmtQty(kg)} kg` : undefined };
    }
    case 'wip': {
      const good = shiftsToday(d).reduce((a, s) => a + s.goodQuantity, 0);
      return { tone: good ? 'ok' : 'neutral', label: good ? 'Producción de hoy' : 'Sin partes hoy', metric: good ? `${fmtQty(good)} u.` : undefined, active: good > 0 };
    }
    case 'delivery': {
      const pend = d.orders.reduce((a, o) => a + Math.max(0, o.balance), 0);
      return { tone: d.orders.length ? 'info' : 'neutral', label: d.orders.length ? `${d.orders.length} OC abiertas` : 'Sin OC abiertas', metric: pend ? `${fmtQty(pend)} u. por entregar` : undefined };
    }
    case 'office': {
      const pending = pendingDocs(d).length;
      const claims = d.receptions.filter(r => r.differenceTreatment === 'Reclamo').length;
      if (pending || claims) return { tone: 'warn', label: [pending && `${pending} sin comprobante`, claims && `${claims} en reclamo`].filter(Boolean).join(' · ') };
      return { tone: 'ok', label: 'Recepciones al día' };
    }
    default:
      return { tone: 'neutral', label: 'Próxima etapa' };
  }
}

/** Recepciones pesadas que todavía no tienen factura/remito vinculado. */
export const pendingDocs = (d: PlantData) => d.receptions.filter(r => !r.remitoNumber);

function isTodayAr(iso: string, today: string): boolean {
  return new Date(iso).toLocaleDateString('en-CA', { timeZone: 'America/Argentina/Buenos_Aires' }) === today;
}

export const AR_TZ = 'America/Argentina/Buenos_Aires';
export const fmtWhen = (iso?: string | null) => {
  if (!iso) return '—';
  const d = new Date(iso);
  const day = d.toLocaleDateString('en-CA', { timeZone: AR_TZ });
  const time = d.toLocaleTimeString('es-AR', { timeZone: AR_TZ, hour: '2-digit', minute: '2-digit' });
  if (day === todayIso()) return `Hoy, ${time}`;
  if (day === addDaysIso(todayIso(), -1)) return `Ayer, ${time}`;
  return `${day.slice(8, 10)}/${day.slice(5, 7)}, ${time}`;
};
export const fmtDayShort = (iso?: string | null) => (iso ? `${iso.slice(8, 10)}/${iso.slice(5, 7)}` : '—');
export const fmtKg = (n?: number | null) => (n == null ? '—' : `${fmtQty(n)} kg`);

// ==================== Avisos (campanita y tablero) ====================

export interface PlantAlert {
  id: string;
  tone: Tone;
  title: string;
  detail?: string;
  path: string;
}

const zonePath = (layout: PlantLayout | null, kind: PlantZone['kind'], fallback: string) => {
  const z = layout?.zones.find(x => x.kind === kind);
  return z ? `/factory/plant?sector=${z.id}` : fallback;
};

const names = (xs: { name: string }[], max = 3) =>
  xs.slice(0, max).map(x => x.name).join(', ') + (xs.length > max ? ` y ${xs.length - max} más` : '');

/** Lo que hay que mirar hoy, de lo más grave a lo menos. */
export function plantAlerts(d: PlantData, layout: PlantLayout | null): PlantAlert[] {
  const out: PlantAlert[] = [];
  const red = d.stock.products.filter(p => p.level === 'rojo');
  const yellow = d.stock.products.filter(p => p.level === 'amarillo');
  const mpLow = d.stock.materials.filter(m => m.level === 'rojo' || m.level === 'amarillo');
  const broken = d.machines.filter(m => m.status === 'FueraDeServicio');
  const overdue = d.overview.filter(o => o.overdueCount > 0);
  const soon = d.overview.reduce((a, o) => a + o.soonCount, 0);
  const pending = pendingDocs(d);
  const claims = d.receptions.filter(r => r.differenceTreatment === 'Reclamo');
  const lateGrinding = d.grinding.filter(g => g.status === 'Enviado' && (Date.now() - new Date(g.sentAt).getTime()) / 86_400_000 > 7);

  broken.forEach(m => out.push({ id: `broken-${m.id}`, tone: 'bad', title: `${m.name} fuera de servicio`, detail: m.notes || undefined, path: '/factory/maintenance' }));
  if (red.length) out.push({ id: 'stock-red', tone: 'bad', title: `${red.length} producto${red.length > 1 ? 's' : ''} sin stock`, detail: names(red), path: '/factory/stock' });
  overdue.forEach(o => out.push({ id: `maint-${o.machineId}`, tone: 'bad', title: `Mantenimiento vencido · ${o.name}`, detail: o.nextDueTask || undefined, path: '/factory/maintenance' }));
  if (mpLow.length) out.push({ id: 'mp-low', tone: mpLow.some(m => m.level === 'rojo') ? 'bad' : 'warn', title: 'Materia prima bajo el mínimo', detail: names(mpLow), path: '/factory/materials' });
  if (yellow.length) out.push({ id: 'stock-yellow', tone: 'warn', title: `${yellow.length} producto${yellow.length > 1 ? 's' : ''} cerca del mínimo`, detail: names(yellow), path: '/factory/stock' });
  if (lateGrinding.length) out.push({ id: 'grinding-late', tone: 'warn', title: 'Molienda afuera hace más de 7 días', detail: lateGrinding.map(g => g.code).join(', '), path: zonePath(layout, 'grinding', '/factory/materials') });
  if (pending.length) out.push({ id: 'docs', tone: 'warn', title: `${pending.length} recepci${pending.length > 1 ? 'ones' : 'ón'} sin factura`, detail: 'Vinculá el comprobante desde Oficina', path: zonePath(layout, 'office', '/factory/materials') });
  if (claims.length) out.push({ id: 'claims', tone: 'info', title: `${claims.length} recepci${claims.length > 1 ? 'ones' : 'ón'} en reclamo al proveedor`, path: zonePath(layout, 'office', '/factory/materials') });
  if (soon) out.push({ id: 'maint-soon', tone: 'info', title: `${soon} mantenimiento${soon > 1 ? 's' : ''} próximo${soon > 1 ? 's' : ''}`, path: '/factory/maintenance' });
  return out;
}
